import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import {
  Building2,
  Eye,
  EyeOff,
  Mail,
  User,
  Phone,
  Globe,
  Lock,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  Loader2,
  Palette
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useEnhancedMerchantRegistration } from '@/hooks/useEnhancedMerchantRegistration'
import { validateMerchantRegisterForm } from '@/utils/validation'

interface MerchantRegistrationFormData {
  // User fields
  email: string
  password: string
  confirm_password: string
  first_name: string
  last_name: string
  phone_number?: string

  // Merchant fields  
  company_name: string
  description?: string
  website?: string
  currency?: string
  country?: string

  // WhiteLabel fields
  custom_domain?: string
  primary_color?: string
  secondary_color?: string
  company_display_name?: string
  support_email?: string
  support_phone?: string
  page_title?: string
  meta_description?: string
  enable_custom_checkout?: boolean
  enable_dashboard_access?: boolean
  enable_api_access?: boolean
  enable_branding_css?: boolean

  accept_terms: boolean
}

export default function MerchantRegister() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [faviconFile, setFaviconFile] = useState<File | null>(null)
  const { registerMerchantWithWhiteLabel, isLoading, error } = useEnhancedMerchantRegistration()

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors }
  } = useForm<MerchantRegistrationFormData>({
    defaultValues: {
      accept_terms: false
    }
  })

  const onSubmit = async (data: MerchantRegistrationFormData) => {
    // Clear previous errors
    setErrors({})

    // Client-side validation
    const validation = validateMerchantRegisterForm({
      email: data.email,
      password: data.password,
      confirm_password: data.confirm_password,
      first_name: data.first_name,
      last_name: data.last_name,
      company_name: data.company_name,
      phone_number: data.phone_number,
      website_url: data.website,
      terms_accepted: data.accept_terms
    })

    if (!validation.isValid) {
      setErrors(validation.errors)
      return
    }

    try {
      // Prepare enhanced registration data
      const enhancedData = {
        // Basic merchant data
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number,
        company_name: data.company_name,
        description: data.description,
        website: data.website,
        currency: data.currency,
        country: data.country,

        // WhiteLabel configuration (if provided)
        whiteLabel: {
          custom_domain: data.custom_domain,
          primary_color: data.primary_color || '#0052cc',
          secondary_color: data.secondary_color || '#6b7280',
          company_display_name: data.company_display_name || data.company_name,
          support_email: data.support_email || data.email,
          support_phone: data.support_phone,
          page_title: data.page_title,
          meta_description: data.meta_description,
          enable_custom_checkout: data.enable_custom_checkout || false,
          enable_dashboard_access: data.enable_dashboard_access !== false,
          enable_api_access: data.enable_api_access !== false,
          enable_branding_css: data.enable_branding_css || false,
          logo: logoFile || undefined,
          favicon: faviconFile || undefined,
        }
      }

      const result = await registerMerchantWithWhiteLabel(enhancedData)

      if (result.success) {
        // Success - redirect or show success message
        console.log('Registration successful:', result)
        // You can add navigation logic here
      }
    } catch (error: any) {
      setErrors({
        general: error.message || 'Registration failed. Please try again.'
      })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="p-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full">
              <Building2 className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Create Merchant Account</h2>
          <p className="mt-2 text-gray-600">
            Join PexiBlocks and start accepting payments
          </p>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* General Error */}
          {(errors.general || error) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-red-800 text-sm">{errors.general || error}</span>
              </div>
            </div>
          )}

          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <User className="h-5 w-5 mr-2 text-orange-600" />
              Personal Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  {...register('first_name', { required: 'First name is required' })}
                  type="text"
                  placeholder="First Name"
                  className={`${errors.first_name || formErrors.first_name ? 'border-red-500' : ''}`}
                />
                {(errors.first_name || formErrors.first_name) && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.first_name || formErrors.first_name?.message}
                  </p>
                )}
              </div>

              <div>
                <Input
                  {...register('last_name', { required: 'Last name is required' })}
                  type="text"
                  placeholder="Last Name"
                  className={`${errors.last_name || formErrors.last_name ? 'border-red-500' : ''}`}
                />
                {(errors.last_name || formErrors.last_name) && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.last_name || formErrors.last_name?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email format'
                  }
                })}
                type="email"
                placeholder="Email Address"
                className={`pl-10 ${errors.email || formErrors.email ? 'border-red-500' : ''}`}
              />
              {(errors.email || formErrors.email) && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.email || formErrors.email?.message}
                </p>
              )}
            </div>

            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                {...register('phone_number')}
                type="tel"
                placeholder="Phone Number (Optional)"
                className="pl-10"
              />
            </div>
          </div>

          {/* Business Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-orange-600" />
              Business Information
            </h3>

            <div>
              <Input
                {...register('company_name', { required: 'Company name is required' })}
                type="text"
                placeholder="Company Name"
                className={`${errors.company_name || formErrors.company_name ? 'border-red-500' : ''}`}
              />
              {(errors.company_name || formErrors.company_name) && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.company_name || formErrors.company_name?.message}
                </p>
              )}
            </div>

            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                {...register('website')}
                type="url"
                placeholder="Website URL (Optional)"
                className="pl-10"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <select
                  {...register('currency')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                >
                  <option value="">Select Currency (Optional)</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                  <option value="JPY">JPY - Japanese Yen</option>
                </select>
              </div>

              <div>
                <select
                  {...register('country')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                >
                  <option value="">Select Country (Optional)</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="GB">United Kingdom</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                  <option value="AU">Australia</option>
                  <option value="JP">Japan</option>
                </select>
              </div>
            </div>

            <div>
              <textarea
                {...register('description')}
                placeholder="Business Description (Optional)"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm resize-none"
              />
            </div>
          </div>

          {/* Security */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Lock className="h-5 w-5 mr-2 text-orange-600" />
              Security
            </h3>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                })}
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                className={`pl-10 pr-10 ${errors.password || formErrors.password ? 'border-red-500' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              {(errors.password || formErrors.password) && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.password || formErrors.password?.message}
                </p>
              )}
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                {...register('confirm_password', { required: 'Please confirm your password' })}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm Password"
                className={`pl-10 pr-10 ${errors.confirm_password || formErrors.confirm_password ? 'border-red-500' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              {(errors.confirm_password || formErrors.confirm_password) && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.confirm_password || formErrors.confirm_password?.message}
                </p>
              )}
            </div>
          </div>

          {/* White-Label Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Palette className="h-5 w-5 mr-2 text-orange-600" />
              Branding & Configuration (Optional)
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  {...register('custom_domain')}
                  type="text"
                  placeholder="Custom Domain (e.g., pay.yourcompany.com)"
                  className="text-sm"
                />
              </div>

              <div>
                <Input
                  {...register('company_display_name')}
                  type="text"
                  placeholder="Display Name (defaults to company name)"
                  className="text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Primary Color
                </label>
                <Input
                  {...register('primary_color')}
                  type="color"
                  defaultValue="#0052cc"
                  className="h-10 w-full"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Secondary Color
                </label>
                <Input
                  {...register('secondary_color')}
                  type="color"
                  defaultValue="#6b7280"
                  className="h-10 w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  {...register('support_email')}
                  type="email"
                  placeholder="Support Email (defaults to your email)"
                  className="text-sm"
                />
              </div>

              <div>
                <Input
                  {...register('support_phone')}
                  type="tel"
                  placeholder="Support Phone (optional)"
                  className="text-sm"
                />
              </div>
            </div>

            <div>
              <Input
                {...register('page_title')}
                type="text"
                placeholder="Page Title (for browser tab)"
                className="text-sm"
              />
            </div>

            <div>
              <textarea
                {...register('meta_description')}
                placeholder="Meta Description (for SEO)"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center space-x-2">
                <input
                  {...register('enable_custom_checkout')}
                  type="checkbox"
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Custom Checkout</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  {...register('enable_dashboard_access')}
                  type="checkbox"
                  defaultChecked={true}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Dashboard Access</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  {...register('enable_api_access')}
                  type="checkbox"
                  defaultChecked={true}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">API Access</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  {...register('enable_branding_css')}
                  type="checkbox"
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Custom CSS</span>
              </label>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo Upload (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Favicon Upload (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFaviconFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                />
              </div>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="space-y-4">
            <div className="flex items-start">
              <input
                {...register('accept_terms', { required: true })}
                type="checkbox"
                className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <p className="text-sm text-gray-700">
                  I agree to the{' '}
                  <Link to="/terms" className="text-orange-600 hover:text-orange-700 underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-orange-600 hover:text-orange-700 underline">
                    Privacy Policy
                  </Link>
                </p>
                {(errors.accept_terms || formErrors.accept_terms) && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.accept_terms || 'You must accept the terms and conditions'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium py-2 px-4 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                Creating Account...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                Create Merchant Account
                <ArrowRight className="ml-2 h-4 w-4" />
              </div>
            )}
          </Button>


        </form>

        {/* Benefits */}
        <div className="mt-8 bg-gray-50 rounded-lg p-6">
          <h4 className="text-sm font-medium text-gray-900 mb-4">Why choose PexiBlocks?</h4>
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
              <span>Secure payment processing</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
              <span>Multiple payment methods</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
              <span>Real-time transaction monitoring</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
              <span>Comprehensive API integration</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}